import React, { useEffect, useState } from "react";
import styles from "./Messages.module.scss";
import SettingsIcon from "../../../assets/icons/SettingsIcon";
import SendIcon from "../../../assets/icons/SendIcon";
import ImageIcon from "../../../assets/icons/ImageIcon";
import MessageSettings from "./Settings/MessageSettings";
import Input from "../../Input/Input";
import Chat from "../../Chat/Chat";
import LoadingContainer from "../../LoadingContainer/LoadingContainer";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../utils/dateUtils";
import { useQuery } from "@tanstack/react-query";
import {
  conversationById,
  messagesList,
  sendMessage,
} from "../../../api/messagesApi";

const Messages = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [messagesData, setMessagesData] = useState(null);
  const [inputValue, setInputValue] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["messagesData"],
    queryFn: messagesList,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchInterval: 5 * 1000,
  });

  const fetchConversation = async (id) => {
    try {
      const response = await conversationById(id, data);
      setChatId(id);
      setMessagesData([...response.data.messages]);
    } catch (error) {
      console.error("Error fetching conversation:", error);
    }
  };

  useEffect(() => {
    if (!chatId) return;

    fetchConversation(chatId);

    const intervalId = setInterval(() => {
      fetchConversation(chatId);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [chatId]);

  const messagesListData = data?.data;

  const sendChatMessage = async () => {
    if (!inputValue.trim()) return;

    try {
      await sendMessage(chatId, { content: inputValue });
      setInputValue("");
      await fetchConversation(chatId);
      refetch();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <div className={styles.messages}>
      <div className={styles.messagesAside}>
        <div className={styles.asideHeader}>
          <Input placeholder={t("messages.searchPlaceholder")} search={true} />
          <button
            className={styles.headerButton}
            onClick={() => setSettings((prev) => !prev)}
          >
            <SettingsIcon />
          </button>
          {settings && (
            <MessageSettings setSettings={setSettings} settings={settings} />
          )}
        </div>
        <div className={styles.messagesList}>
          {isLoading ? (
            <LoadingContainer />
          ) : (
            <>
              <h4 className={styles.messagesListInfo}>
                {t("messages.foundDialogs")} {messagesListData?.length}
              </h4>
              <div className={styles.messagesItems}>
                {messagesListData?.map((message) => (
                  <div
                    key={message.id}
                    className={styles.messageItem}
                    onClick={() => fetchConversation(message.id)}
                  >
                    <div className={styles.itemHeader}>
                      <h3>{message.user_display_name}</h3>
                      <span className={styles.headerDate}>
                        {formatDate(message.last_message_at)}
                      </span>
                      {!message.is_read && (
                        <span className={styles.unreadDot}></span>
                      )}
                    </div>
                    <p className={styles.itemContent}>
                      {message.last_message.content}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <div className={styles.chat}>
        {chatId && messagesData ? (
          <Chat chatId={chatId} messagesData={messagesData} />
        ) : (
          <div className={styles.chatContent}>{t("messages.chooseDialog")}</div>
        )}
        <div className={styles.chatBottom}>
          <button className={styles.buttonImage}>
            <ImageIcon />
          </button>
          <Input
            placeholder={t("messages.inputMessagePlaceholder")}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <button className={styles.headerButton} onClick={sendChatMessage}>
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Messages;
